import { useUserStore } from '@/store/userStore';
import { needsOnboardingIntake } from '@/utils/onboarding';

/**
 * Authentication middleware for Vue Router
 * @param {import('vue-router').RouteLocationNormalized} to - Target route
 * @param {import('vue-router').RouteLocationNormalized} from - Current route
 * @param {import('vue-router').NavigationGuardNext} next - Navigation guard function
 */
export async function checkAuth(to, from, next) {
  const userStore = useUserStore();
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);

  // Get the most permissive parent roles (if any)
  const parentRoles = to.matched[0]?.meta?.roles || [];

  // Get the most specific child roles (if any)
  const childRoles = to.matched.slice(-1)[0]?.meta?.roles;

  // If child specifies roles, use those (more restrictive)
  // Otherwise, fall back to parent roles
  const requiredRoles = childRoles || parentRoles;

  // Allow access to non-auth routes
  if (!requiresAuth) {
    next();
    return;
  }

  // Initialize user store if needed
  try {
    if (!userStore.isAuthenticated) {
      const initialized = await userStore.init();
      if (!initialized) {
        next('/login');
        return;
      }
    }

    // Ensure we have user data
    if (!userStore.userData || !userStore.currentOrganization) {
      try {
        await userStore.fetchUserData();
      } catch (error) {
        console.error('❌ Failed to fetch user data:', error);
        next('/login');
        return;
      }
    }

    // Check role permissions if needed (super_admin bypasses all role checks)
    if (requiredRoles.length > 0) {
      const userRole = userStore.currentOrganization?.role || 'member';
      if (userRole !== 'super_admin' && !requiredRoles.includes(userRole)) {
        console.log('⛔ User role not authorized for this route');
        next(userRole === 'installer' ? '/vandaag' : '/');
        return;
      }
    }

    // Platform admin guard — checks ALL memberships, not just current org.
    // Mirrors the backend `requirePlatformAdmin` middleware. The actual
    // security check still happens server-side; this is just UX.
    const requiresPlatformAdmin = to.matched.some((record) => record.meta.platformAdmin);
    if (requiresPlatformAdmin) {
      const memberships = [
        ...(userStore.organizations || []),
        ...(userStore.userData?.organizationMemberships || []),
        ...(userStore.userData?.organizations || []),
      ];
      const isPlatformAdmin =
        userStore.currentOrganization?.role === 'super_admin' ||
        memberships.some((m) => m?.role === 'super_admin');
      if (!isPlatformAdmin) {
        next('/');
        return;
      }
    }

    // Super admins skip customer onboarding — they should be able to enter
    // any team, including ones that have not finished intake yet.
    const skipOnboardingRedirect = to.matched.some((record) => record.meta.skipOnboardingRedirect);
    if (!skipOnboardingRedirect && userStore.currentOrganization) {
      const role = userStore.currentOrganization.role;
      if (role !== 'super_admin' && needsOnboardingIntake(userStore.currentOrganization, role)) {
        next('/onboarding');
        return;
      }
    }

    next();
  } catch (error) {
    console.error('❌ Auth middleware error:', error);
    next('/login');
  }
}
